import {
  ProviderRequest,
  ProviderResponse,
  ProviderResponseSchema,
} from '@/apis/Providers/types.ts'
import { TransformedProduct } from '@/app/pages/providers/components/ProductForm/add-product.schema'
import { UpdateProviderStorePayload } from '@/app/pages/providers/components/configureStore/configure-store.types'
import { RequestResponse } from '@/common/types_and_interfaces.ts'
import { parseRequestError } from '@/common/utilities.ts'
import { ProviderPreviewSettingsSchema } from '@/schemas/provider.schema'
import axios from 'axios'
import { z } from 'zod'

export const SchoolApplicationResponseSchema = z.object({
  message: z.string(),
  data: z
    .object({
      id: z.number(),
      provider_id: z.number(),
      program_details: z.any(),
    })
    .transform((data) => ({
      school_application_id: data.id,
      provider_id: data.provider_id,
      program_details: data.program_details,
    })),
})

export type SchoolApplicationRequest = {
  provider_id: number
  school_application_id: number
  program_id: number
}

export type SchoolApplicationResponse = z.infer<
  typeof SchoolApplicationResponseSchema
>

const ResendInvitationResponseSchema = z.object({
  message: z.string(),
})

export type ResendInvitationRequest = {
  provider_id: number
  program_id: number
}

export const sendInviteProvider = async (
  payload: ProviderRequest
): Promise<RequestResponse<ProviderResponse>> => {
  try {
    const response = await axios.post('/admin/providers', payload)
    return {
      data: ProviderResponseSchema.parse(response.data),
      error: null,
    }
  } catch (error) {
    return {
      data: null,
      error: parseRequestError(error),
    }
  }
}

export type AddProviderItemReq = {
  provider_id: string
  program_id: number
  payload: TransformedProduct
  global_item_category_id: number
  global_item_id: number
  type: number
}

const AddProductResponseSchema = z.object({
  marketplace: z.string(),
})

export const addProviderItem = async (requestBody: AddProviderItemReq) => {
  const {
    provider_id,
    program_id,
    payload,
    global_item_category_id,
    global_item_id,
    type,
  } = requestBody

  const formData = new FormData()

  formData.append('program_id', program_id.toString())
  formData.append('global_item_category_id', global_item_category_id.toString())
  formData.append('global_item_id', global_item_id.toString())
  formData.append('type', type.toString())

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (key === 'file' && value instanceof File) {
        formData.append(key, value)
      } else {
        formData.append(key, value.toString())
      }
    }
  })

  const response = await axios.post(
    `providers/${provider_id}/items`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )

  return AddProductResponseSchema.parse(response.data)
}

// Utility to convert file to base64 (copied from users/requests.ts)
const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })
}

export const updatePreviewSettings = async (
  providerId: string,
  payload: UpdateProviderStorePayload
) => {
  if (payload.logo_image instanceof File) {
    payload.logo_image = await convertFileToBase64(payload.logo_image)
  }

  const response = await axios.put(
    `provider/${providerId}/update_preview_settings`,
    payload
  )
  return ProviderPreviewSettingsSchema.parse(response.data)
}
export const updateProviderInformation = async (
  providerId: string,
  payload: UpdateProviderStorePayload
) => {
  if (payload.logo_image instanceof File) {
    payload.logo_image = await convertFileToBase64(payload.logo_image)
  }

  const response = await axios.put(`provider/${providerId}`, payload)
  return response.data
}

export const submitPreviewSettings = async (
  providerId: string,
  programId: number
) => {
  const response = await axios.put(
    `provider/${providerId}/submit_preview_settings`,
    {
      program_id: programId,
    }
  )
  return response.data
}

// Assume ProviderEndpoints.item is '/providers/:provider_id/items/:item_id'
export type UpdateProviderItemReq = {
  provider_id: number
  item_id: number
  payload: TransformedProduct
}

export const updateProviderItem = async (
  requestBody: UpdateProviderItemReq
) => {
  const payload = {
    ...requestBody.payload,
    keyword_tags: requestBody.payload.keyword_tags?.join(',') || '',
  } // allow file_image property

  let base64File
  if (requestBody.payload.file instanceof File) {
    base64File = await convertFileToBase64(requestBody.payload.file)
  }

  const response = await axios.put(
    `providers/${requestBody.provider_id}/items/${requestBody.item_id}`,
    { ...payload, file: base64File }
  )

  return response.data
}

export const ProviderAvailableCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
  status: z.union([z.literal(0), z.literal(1)]),
})

export type ProviderAvailableCategory = z.infer<
  typeof ProviderAvailableCategorySchema
>

export async function GETProviderAvailableCategories(
  globalOnId: number,
  lang: string = 'en'
) {
  const response = await axios.get('providers/categories', {
    params: {
      global_on_id: globalOnId,
      i18n: lang,
    },
  })
  return z.array(ProviderAvailableCategorySchema).parse(response.data)
}
export type ResendInvitationResponse = z.infer<
  typeof ResendInvitationResponseSchema
>

export const resendInvitationProvider = async (
  payload: ResendInvitationRequest,
  globalOnId: number
): Promise<RequestResponse<ResendInvitationResponse>> => {
  try {
    const response = await axios.post(
      `/admin/providers/resend_invitation?global_on_id=${globalOnId}`,
      payload
    )

    return {
      data: ResendInvitationResponseSchema.parse(response.data),
      error: null,
    }
  } catch (error) {
    return {
      data: null,
      error: parseRequestError(error),
    }
  }
}

export const getSchoolApplication = async (
  request: SchoolApplicationRequest
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<SchoolApplicationResponse | { message?: string; error: any }> => {
  const { provider_id, school_application_id, program_id } = request

  try {
    const response = await axios.get(
      `/admin/provider/${provider_id}/school_applications/${school_application_id}`,
      {
        params: {
          program_id,
        },
      }
    )

    return SchoolApplicationResponseSchema.parse(response.data)
  } catch (error) {
    return {
      message: 'Failed to fetch school application.',
      error: parseRequestError(error),
    }
  }
}
